import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';

import EmailIcon from '@components/icons/Email.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import PlusBalanceSvg from '@components/icons/PlusBalance.svg';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation } from '@tanstack/react-query';
import { OrganizationsService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import React, { useState } from 'react';

import { Content } from './styles/Content';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
};

export const InviteUserModal = ({ onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const [email, setEmail] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>(['']);
  const { organization } = useAuthContext();

  const { openModal } = useModalContext();

  const oneMoreTeam = async () => {
    if (!teamIds[teamIds.length - 1]) {
      return;
    }
    const newTeamIds = [...teamIds, ''];
    setTeamIds(newTeamIds);
  };

  const changeTeamIds = async (index: number, value: string | undefined) => {
    const newTeamIds = [...teamIds];
    if (value) {
      newTeamIds[index] = value;
    } else {
      newTeamIds.splice(index, 1);
    }
    setTeamIds(newTeamIds);
  };

  const createInvitationMutation = useMutation({
    mutationFn: (teamIdsFiltered: string[]) =>
      call(() =>
        OrganizationsService.createInvitation({
          oid: organization?.id ?? '',
          requestBody: { email: email.trim().toLowerCase(), teamIds: teamIdsFiltered },
        }),
      ),
    onSuccess,
    onError: notifyApiCallError,
  });
  const isSubmitting = createInvitationMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createInvitationMutation.mutate(teamIds.filter((teamId: string) => teamId && teamId !== ''));
  };

  const firstOptions = (organization?.orgTeams ?? []).filter((team) => {
    return !teamIds.includes(team.id) || teamIds[0] === team.id;
  });

  const teams = (firstOptions || [])
    .map((role) => ({ value: role.id, label: role.name }))
    .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

  const openInviteUsersByCsvModal = () => {
    openModal(ModalNamesEnum.InviteUsersByCsvModal, {
      onSuccess,
    });
  };

  return (
    <ModalWrapper width="360px">
      <ModalTitle>Invite Member</ModalTitle>
      <Content>
        <form action="#" onSubmit={handleSubmit}>
          <div className="inputWrapper">
            <Input
              icon={EmailIcon}
              required
              value={email}
              type="email"
              label="Email"
              name="email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              error={isSubmitting && !email}
            />
            <div className="additional-team">
              <Select
                value={teamIds[0]}
                options={teams}
                onChange={(_, value) => changeTeamIds(0, value)}
                label="Team"
                required
                name="role"
              />
              {teamIds.length < 5 ? (
                <>
                  <div className="w-[10px]"></div>
                  <IconButtonClean type="button" title="Add team" onClick={oneMoreTeam}>
                    <PlusBalanceSvg />
                  </IconButtonClean>
                </>
              ) : null}
            </div>

            {teamIds.map((teamId, index) => {
              if (index === 0) {
                return;
              }
              const options = (organization?.orgTeams ?? []).filter((team) => {
                return !teamIds.includes(team.id) || teamIds[index] === team.id;
              });
              return (
                <div key={teamId} className="additional-team">
                  <Select
                    key={`team-${index}`}
                    value={teamIds[index]}
                    options={options.map((team) => ({ value: team.id, label: team.name })) ?? []}
                    onChange={(_, value) => changeTeamIds(index, value)}
                    label="Team"
                    required
                    name="role"
                  />
                  <TrashBin
                    style={{ marginLeft: '10px', cursor: 'pointer', color: 'var(--color-text-muted)' }}
                    onClick={() => {
                      setTeamIds((prev) => prev.filter((v) => v !== teamId));
                    }}
                  />
                </div>
              );
            })}

            <OutlinedButton type="button" style={{ width: 'auto' }} onClick={openInviteUsersByCsvModal}>
              Bulk invite by CSV
            </OutlinedButton>
          </div>

          <div className="buttonWrapper">
            <OutlinedButton className="w-full" onClick={onCancel}>
              Cancel
            </OutlinedButton>
            <Button
              className="w-full"
              type="submit"
              disabled={!teamIds.length || !teamIds[0].length || !email || isSubmitting}
            >
              Invite Member
            </Button>
          </div>
        </form>
      </Content>
    </ModalWrapper>
  );
};
